import React from "react";
import { color, fonts } from "@ui";
import t from "../../../i18n";
import formatNumber from "@ui/i18n/formatNumber";
import { useSetups } from "../../../api/useSetups";
import { useProjectCategories } from "../../../api/useProjectCategories";
import { CalculatorLine, FilterLine, ListCheck, LockLine } from "@ui/IconV2/react";
import { css, cx } from "@linaria/core";
import type { ActivationFilter } from "../../../api/useActivationFilters";
import type { MetrologyCrossFilters } from "../../../api/useMetrologyCrossFilters";
import LinkMemo from "@ui/Link";
import { translationLocale } from "../../../i18n/locale";
import Text from "@ui/Text";
import { quantityRoles, type Quantity } from "../../../api/useQuantities";
import { toPath } from "../../../router/helpers/path";
import Button from "@ui/Button";
import { useCanUserDoOnProject } from "../../canUserDo";
import { spacings } from "@ui/Theme";
import type { AlertType } from "./useQuantityAlerts";
import AlertMessage from "./AlertMessage";
import { useDateRange } from "../Analysis/useSelectedAnalyses";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import Ellipsis from "@ui/Ellipsis";
import { getCheckQuantityUsedInSetupsOrFormulas } from "diagnostics-domain/quantity/usage";
import { useFormulaVersions, type FormulaVersion } from "../../../api/quantity/formula-version";
import {
  getBaseFormulaApplicationPeriod,
  getFormulaVersionApplicationPeriod,
  getFormulaVersionAt,
} from "../../../domain/formula-version";
import { useSource } from "../../../api/useSources";
import { useProject } from "../../../api/useProjects";
import formatDate from "../../../ui/i18n/formatDate";

interface P {
  // all wrapped in an options property to match the OverlayComponent signature in ItemSelector
  options: {
    quantity: Quantity;
    projectId: string;
    alertMessageOptions: {
      allAnalysesMode: boolean;
      alertPercentage: number;
      alertCount: number;
      analysisCount: number;
      isInAlert: boolean;
      alertType: AlertType;
    };
    activationFilter?: ActivationFilter;
    crossFilters?: MetrologyCrossFilters;
    onEditQuantity: (q: Quantity, focusFormulaeView?: boolean) => void;
    onDeleteQuantity: (q: Quantity) => void;
    isDrawerOpen: boolean;
  };
}

const getQuantityTypeI18n = (type: Quantity["type"]) => {
  switch (type) {
    case "FORMULA":
      return t("measuresSymptoms.quantityIdentityCard.type.calculated_value");
    case "MEASURE":
      return t("measuresSymptoms.quantityIdentityCard.type.measure");
    default:
      throw new Error(type satisfies never);
  }
};

export function QuantityGroupCard({
  name,
  alertMessage,
}: {
  name: string;
  alertMessage: React.ReactNode;
}) {
  return (
    <div className={wrapperCss}>
      <div className={quantityIDName}>{name}</div>
      <div className={divider} />
      {alertMessage}
    </div>
  );
}

function QuantityIdentityCard({
  options: {
    quantity,
    projectId,
    alertMessageOptions: {
      allAnalysesMode,
      alertType,
      alertPercentage,
      alertCount,
      analysisCount,
      isInAlert,
    },
    activationFilter,
    crossFilters,
    onEditQuantity,
    onDeleteQuantity,
    isDrawerOpen = false,
  },
}: P) {
  const [dateRange] = useDateRange({
    scope: projectId,
  });
  const setups = useSetups(projectId);
  const setup =
    dateRange.pivot === null ? null : getAnalysisSetup(setups, { date: dateRange.pivot });

  const setupName = setup?.name;
  const roles = quantityRoles(quantity, setup ? [setup] : []);
  const equipments = useProjectCategories(projectId, "EQUIPMENT");
  const quantityEquipments = quantity.equipmentIds
    .map(id => equipments.find(e => e.id === id)?.name)
    .filter(Boolean);
  const isUserSetuperOrAbove = useCanUserDoOnProject("PROJECT_SETUP", projectId);
  const canWrite = useCanUserDoOnProject("PROJECT_WRITE", projectId);
  const offset = setup?.simulationOutputs.find(so => so.quantityId === quantity.id)?.offsetExpected;
  const sigma = setup?.diagnosisInputs.find(
    di => di.quantityId === quantity.id && di.enabled,
  )?.sigma;

  const { hasImpactOnSetups } = getCheckQuantityUsedInSetupsOrFormulas(setups, quantity);

  const disabledDeleteFormulaManuallyLocked =
    quantity.formula !== null && quantity.locked && !isUserSetuperOrAbove;
  const disabledDeleteUsedInSetup = hasImpactOnSetups;
  const isDeletable =
    canWrite && !disabledDeleteFormulaManuallyLocked && !disabledDeleteUsedInSetup;

  const showLockInfo = quantity.type === "FORMULA" && quantity.locked;
  const isEditable = canWrite;
  const showDividerBeforeFormulaActions = isEditable;
  const formatFilter = React.useCallback(
    (filter: { min: number | null; max: number | null; maxStd: number | null } | undefined) =>
      [
        [filter?.min, t("project.filters.table.definitions.column.min_value")],
        [filter?.max, t("project.filters.table.definitions.column.max_value")],
        [filter?.maxStd, t("project.filters.table.definitions.column.max_std")],
      ]
        .map(([value, label]) =>
          typeof value === "number"
            ? `${label}${translationLocale === "fr" ? " :" : ":"} ${formatNumber(Number(value), {
                unit: quantity.unit ?? undefined,
              })}`
            : null,
        )
        .filter(Boolean)
        .join(", "),
    [quantity.unit],
  );

  const formattedMetrologyFilter = React.useMemo(
    () =>
      formatFilter({
        min: quantity.minMetrology,
        max: quantity.maxMetrology,
        maxStd: quantity.maxStdMetrology,
      }),
    [formatFilter, quantity.maxMetrology, quantity.maxStdMetrology, quantity.minMetrology],
  );

  const formattedActivationFilter = React.useMemo(
    () => formatFilter(activationFilter),
    [activationFilter, formatFilter],
  );

  // TODO export this to a utils file or separate component
  const shouldDisplayAlertMessage = allAnalysesMode === false && (isInAlert || alertCount !== 0);
  const formattedPercentage =
    alertPercentage > 0.5
      ? formatNumber(alertPercentage, {
          precision: 0,
        })
      : "<1";
  const currentMessage = shouldDisplayAlertMessage
    ? isInAlert
      ? alertType === "measures"
        ? t("quantitySelector.quantity.alert.measures.current")
        : t("quantitySelector.quantity.alert.symptoms.current")
      : null
    : null;
  const message = shouldDisplayAlertMessage
    ? alertCount
      ? alertType === "measures"
        ? t("quantitySelector.quantity.alert.measures.inRange", {
            alertCount,
            analysisCount,
            alertPercentage: formattedPercentage,
          })
        : t("quantitySelector.quantity.alert.symptoms.inRange", {
            alertCount,
            analysisCount,
            alertPercentage: formattedPercentage,
          })
      : null
    : null;
  const alertMessage = shouldDisplayAlertMessage ? (
    <AlertMessage currentMessage={currentMessage} message={message} alertType={alertType} />
  ) : null;

  const source = useSource(projectId);
  const project = useProject(projectId);
  const formulaVersions = useFormulaVersions(projectId);
  let formulaVersion: FormulaVersion | null = null;
  let formulaApplicationPeriod: { from: Date; to: Date | null } | null = null;
  if (quantity) {
    const quantityFormulaVersions = formulaVersions.filter(fv => fv.quantityId === quantity.id);
    formulaVersion = getFormulaVersionAt(quantity.id, formulaVersions, new Date());
    if (formulaVersion) {
      formulaApplicationPeriod = getFormulaVersionApplicationPeriod(
        formulaVersion,
        quantityFormulaVersions,
      );
    } else {
      if (quantityFormulaVersions.length === 0) {
        formulaApplicationPeriod = null;
      } else {
        const startsAt =
          source?.minSnapshotDate ?? project?.analysesAggregate?.earliestActiveAnalysis?.date;
        formulaApplicationPeriod = getBaseFormulaApplicationPeriod(
          startsAt ? new Date(startsAt) : new Date(),
          quantityFormulaVersions,
        );
      }
    }
  }
  const formulaPeriodLabel = formulaApplicationPeriod
    ? t(
        "project.settings.quantity.side_panel.section.formula_input.usage_period",
        formatDate(formulaApplicationPeriod.from, "date", project?.timezone ?? null),
        formulaApplicationPeriod.to === null
          ? null
          : formatDate(formulaApplicationPeriod.to, "date", project?.timezone ?? null),
      )
    : null;
  const displayedFormula = formulaVersion?.formula ?? quantity.formula;
  const formattedDisplayedFormula = isNaN(Number(displayedFormula))
    ? displayedFormula
    : formatNumber(Number(displayedFormula));

  return (
    <>
      <div className={wrapperCss}>
        <div className={quantityIDName}>{quantity.name}</div>
        {quantity.qoiOrder !== null && (
          <div className={quantityIsQoi}>
            {t("measuresSymptoms.quantitiy.quantityIdentityCard.qoi", { order: quantity.qoiOrder })}
          </div>
        )}
        {quantity.processIndicatorOrder !== null && (
          <div className={quantityIsQoi}>
            {t("measuresSymptoms.quantitiy.quantityIdentityCard.process", {
              order: quantity.processIndicatorOrder,
            })}
          </div>
        )}
        {quantity.description && (
          <div className={quantityIDDescription}>{quantity.description}</div>
        )}
        {alertMessage && (
          <>
            <div className={divider} />
            <div className={alertMessageCss}>{alertMessage}</div>
          </>
        )}
        <div className={divider} />
        <div className={quantityIDCardRow}>
          <div className={quantityIDCardProperty}>
            {t("measuresSymptoms.quantitiy.quantityIdentityCard.type")}
          </div>
          <span className={quantityTypeContainer}>
            {getQuantityTypeI18n(quantity.type)}
            {quantity.type === "FORMULA" && (
              <>
                <CalculatorLine size="s" />
                {quantity.formulaVersionCount > 0 && (
                  <Button
                    variant="link"
                    leftIcon={<ListCheck />}
                    onClick={() => onEditQuantity(quantity, true)}
                  >
                    {t(
                      "quantities.formula_version.version_count",
                      quantity.formulaVersionCount + 1,
                    )}
                  </Button>
                )}
              </>
            )}{" "}
          </span>
          {showLockInfo && (
            <div className={lockedFormulaTextGroup}>
              <LockLine size="s" />
              <span className={lockedFormulaLabel}>
                {t("measuresSymptoms.quantitiy.quantityIdentityCard.lockedFormula")}
              </span>
            </div>
          )}
        </div>
        {quantity.unit && (
          <div className={quantityIDCardRow}>
            <div className={quantityIDCardProperty}>
              {t("measuresSymptoms.quantitiy.quantityIdentityCard.unit")}
            </div>
            {quantity.unit}
          </div>
        )}
        {quantity.type === "MEASURE" ? (
          <div className={quantityIDCardRow}>
            <div className={quantityIDCardProperty}>
              {t("measuresSymptoms.quantitiy.quantityIdentityCard.sensorTag")}
            </div>
            <Ellipsis>{quantity.sensor.tag}</Ellipsis>
          </div>
        ) : null}
        {quantity.type === "FORMULA" ? (
          <div className={quantityIDCardRow}>
            <div className={quantityIDCardProperty}>
              {t("measuresSymptoms.quantitiy.quantityIdentityCard.formule")}
            </div>
            <div className={quantityExpression}>
              {formulaPeriodLabel}
              {formulaPeriodLabel ? <br /> : null}
              {formattedDisplayedFormula}
            </div>
          </div>
        ) : null}
        {roles.has("others") ? null : (
          <>
            <div className={divider} />
            <div className={quantityIDCardRow}>
              <div className={quantityIDCardProperty}>
                {t("measuresSymptoms.quantitiy.quantityIdentityCard.roles")}
              </div>
            </div>
            <div>
              <ul className={rolesCss}>
                {Array.from(roles).map(role => (
                  <li className={roleCss} key={role}>
                    {role === "simulationInput" ? (
                      <>
                        {t("measuresSymptoms.quantitiy.quantityIdentityCard.role.simulationInput")}
                      </>
                    ) : null}
                    {role === "simulationOutput" ? (
                      <>
                        {t("measuresSymptoms.quantitiy.quantityIdentityCard.role.simulationOutput")}
                        <div className={quantityIDCardRow}>
                          {`${t("measuresSymptoms.quantitiy.quantityIdentityCard.offset")} = `}
                          {formatNumber(Number(offset))} {quantity.unit}
                        </div>
                      </>
                    ) : null}
                    {role === "observable" ? (
                      <>
                        {t("measuresSymptoms.quantitiy.quantityIdentityCard.role.observable")}
                        <div className={quantityIDCardRow}>
                          {`${t("measuresSymptoms.quantitiy.quantityIdentityCard.sigma")} = `}
                          {formatNumber(Number(sigma))} {quantity.unit}
                        </div>
                      </>
                    ) : null}
                  </li>
                ))}
                {setupName ? (
                  <div className={ellipse2lineClamp} title={setupName}>
                    {t(
                      "measuresSymptoms.quantitiy.quantityIdentityCard.role.setupName",
                      <Text type="body2SemiBold" as="span">
                        {setupName}
                      </Text>,
                    )}
                  </div>
                ) : null}
              </ul>
            </div>
          </>
        )}
        {(quantity.systemId || quantityEquipments.length > 0) && <div className={divider} />}
        {quantity.systemId ? (
          <div className={quantityIDCardRow}>
            <div className={quantityIDCardProperty}>
              {t("measuresSymptoms.quantitiy.quantityIdentityCard.system")}
            </div>
            {quantity.systemId}
          </div>
        ) : null}
        {quantityEquipments.length > 0 ? (
          <div className={quantityIDCardRow}>
            <div className={quantityIDCardProperty}>
              {t("measuresSymptoms.quantitiy.quantityIdentityCard.equipments")}
            </div>
            {quantityEquipments.join(", ")}
          </div>
        ) : null}
        {formattedActivationFilter && <div className={divider} />}
        {formattedActivationFilter && (
          <>
            <div className={quantityIDCardRow}>
              <div className={cx(quantityIDCardProperty, bottomSpacing)}>
                <span className={activationFilterTitleContent}>
                  {t("project.filters.title")}
                  <FilterLine />:
                </span>
              </div>
            </div>
            <div className={quantityIDCardRow}>{formattedActivationFilter}</div>
          </>
        )}
        {((crossFilters && crossFilters.length > 0) || formattedMetrologyFilter.length > 0) && (
          <>
            <div className={divider} />
            <div className={quantityIDCardRow}>
              <div className={cx(quantityIDCardProperty, bottomSpacing)}>
                {t("quantities.metrologyFilter")}
              </div>
            </div>
            <div className={quantityIDCardRow}>
              <ul
                className={metrologyFilterList}
                data-item-count={
                  (formattedMetrologyFilter.length > 0 ? 1 : 0) + (crossFilters?.length ?? 0)
                }
              >
                {formattedMetrologyFilter.length > 0 && <li>{formattedMetrologyFilter}</li>}
                {crossFilters &&
                  crossFilters.length > 0 &&
                  crossFilters
                    .slice(0, 2)
                    .map(filter => (
                      <li key={`${filter.evaluatedQuantityName}_${filter.invalidatedQuantityName}`}>
                        {t(
                          "quantities.crossFilter.info",
                          filter.evaluatedQuantityName,
                          formatFilter(filter),
                        )}
                      </li>
                    ))}
              </ul>
            </div>
            {crossFilters && crossFilters.length > 2 && (
              <div className={quantityIDCardRow}>
                <LinkMemo
                  to={toPath("/projects/:projectId/settings/quantities/crossFilters", {
                    projectId,
                  })}
                  className={seeMore}
                >
                  {t("common.see_more")} ({crossFilters.length - 2})
                </LinkMemo>
              </div>
            )}
          </>
        )}
        {showDividerBeforeFormulaActions && <div className={divider} />}
        {isEditable && (
          <div className={quantityActionsCss}>
            <div>
              <Button
                variant="link"
                onClick={() => onEditQuantity(quantity)}
                slim
                disabled={isDrawerOpen}
                title={
                  isDrawerOpen ? t("quantitySelector.actionDisabledBecauseDrawerOpen") : undefined
                }
              >
                {t("measuresSymptoms.quantitiy.quantityIdentityCard.editFormulaAction")}
              </Button>
              {setupName && (
                <Button
                  variant="destructiveLink"
                  slim
                  disabled={!isDeletable}
                  title={
                    isDeletable
                      ? undefined
                      : disabledDeleteUsedInSetup
                        ? t(
                            "measuresSymptoms.quantitiy.quantityIdentityCard.deleteFormulaAction.disabled.usedInSetup",
                            setupName,
                          )
                        : t(
                            "measuresSymptoms.quantitiy.quantityIdentityCard.deleteFormulaAction.disabled.locked",
                          )
                  }
                  onClick={() => onDeleteQuantity(quantity)}
                >
                  {t("measuresSymptoms.quantitiy.quantityIdentityCard.deleteFormulaAction")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default React.memo(QuantityIdentityCard);

//#region CSS
const wrapperCss = css`
  font: ${fonts.body2};
  min-width: 220px;
  max-width: 300px;
`;

const quantityIDName = css`
  color: ${color.carbon900};
  font: ${fonts.body2SemiBold};
  text-overflow: ellipsis;
`;

const quantityIsQoi = css`
  font: ${fonts.body2};
  color: ${color.carbon900};
`;

const quantityIDDescription = css`
  color: ${color.carbon900};
  padding-top: 10px;
`;

const quantityIDCardRow = css`
  display: flex;
`;

const quantityIDCardProperty = css`
  font: ${fonts.body2SemiBold};
  margin-right: 4px;
  white-space: nowrap;
`;

const ellipse2lineClamp = css`
  padding-top: 5px;
  word-break: break-all;

  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-inline-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  vertical-align: sub;
`;

const bottomSpacing = css`
  margin-bottom: 4px;
`;

const divider = css`
  border-top: 1px solid;
  border-top-color: ${color.titanium200};
  margin-bottom: 8px;
  margin-top: 12px;
`;

const alertMessageCss = css`
  color: ${color.carbon800};
`;

const quantityExpression = css`
  overflow-wrap: anywhere;
`;

const rolesCss = css`
  display: flex;
  flex-direction: column;
`;

const roleCss = css`
  display: flex;
  flex-direction: column;
  padding-top: 5px;
`;

const quantityTypeContainer = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const metrologyFilterList = css`
  list-style: initial;
  padding-left: 24px;

  &[data-item-count="1"] {
    list-style: none;
    padding-left: 0;
  }
`;

const seeMore = css`
  color: ${color.lightNitrogen500_Main};
`;

const activationFilterTitleContent = css`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const quantityActionsCss = css`
  display: flex;
  align-self: stretch;

  & > div {
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: ${spacings.xs};
  }
`;

const lockedFormulaTextGroup = css`
  display: flex;
  gap: ${spacings.xxs};
  margin-left: auto;
`;

const lockedFormulaLabel = css`
  color: ${color.carbon800};
`;
//#endregion CSS
